import { useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import moment from 'moment';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import React from 'react';
import { logError } from '@src/app/actions/userActions';
import {
    allocateUniquePdfFileName,
    BILL_PDF_CONCURRENCY,
    generateBillPdfBlob,
    preloadBillImages,
    resolveBillHtml,
    runWithConcurrency,
} from '@utilities/billPdfGenerator';
import ReportProgressModal, {
    type ReportProgressItem,
    type ReportProgressPhase,
    type ReportProgressSummary,
} from '../components/ReportProgressModal';

type ReportRow = {
    orderId?: string;
    orderSeqNo?: string;
    sequenceNo?: string;
    uoId?: string;
};

type ModalState = {
    open: boolean;
    phase: ReportProgressPhase;
    items: ReportProgressItem[];
    summary: ReportProgressSummary | null;
    progressOverride: string | null;
};

const INITIAL_MODAL_STATE: ModalState = {
    open: false,
    phase: 'confirm',
    items: [],
    summary: null,
    progressOverride: null,
};

const getPdfBaseName = (row: ReportRow) => {
    const base = row?.orderSeqNo || row?.sequenceNo || row?.orderId || 'bill';
    return String(base).replace(/[\\/:*?"<>|]/g, '-');
};

const getBillLabel = (row: ReportRow) => getPdfBaseName(row);

export const collectDownloadBillItems = (rows: ReportRow[] | null | undefined): ReportProgressItem[] =>
    (rows || [])
        .filter((row) => row?.orderId && row.orderId !== 'Total:')
        .map((row) => ({
            id: row.orderId!,
            label: getBillLabel(row),
            status: 'pending' as const,
        }));

const buildZipFilename = (isRoomReport: boolean, startDate: Date, endDate: Date) =>
    `[${isRoomReport ? 'Room' : 'Food'}]${moment(startDate).format('D-MMM-YYYY')} - ${moment(endDate).format('D-MMM-YYYY')}-bills.zip`;

export type UseDownloadBillsOptions = {
    reports: any[];
    restaurant: any;
    isRoomReport: boolean;
    startDate: Date;
    endDate: Date;
    dispatch: (action: ReturnType<typeof logError>) => void;
};

export const useDownloadBills = ({
    reports,
    restaurant,
    isRoomReport,
    startDate,
    endDate,
    dispatch,
}: UseDownloadBillsOptions) => {
    const inFlightRef = useRef(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [modalState, setModalState] = useState<ModalState>(INITIAL_MODAL_STATE);

    const updateItem = (id: string, patch: Partial<ReportProgressItem>) => {
        setModalState((prev) => ({
            ...prev,
            items: prev.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
        }));
    };

    const openDownloadBills = () => {
        if (isDownloading || inFlightRef.current) {
            return;
        }
        const items = collectDownloadBillItems(reports);
        if (!items.length) {
            return;
        }
        setModalState({ open: true, phase: 'confirm', items, summary: null, progressOverride: null });
    };

    const handleConfirm = async () => {
        const billRows = reports.filter((row) => row?.orderId && row.orderId !== 'Total:');
        if (!billRows.length || inFlightRef.current) {
            return;
        }

        inFlightRef.current = true;
        setIsDownloading(true);
        setModalState((prev) => ({
            ...prev,
            phase: 'running',
            summary: null,
            progressOverride: 'Preparing assets…',
            items: prev.items.map((item) => ({
                ...item,
                status: 'pending',
                message: undefined,
                detail: undefined,
            })),
        }));

        const zip = new JSZip();
        let succeeded = 0;
        let failed = 0;
        const errors: Array<{ id: string; message: string }> = [];
        const usedFileNames = new Map<string, number>();
        const author = String(restaurant?.name || '');

        try {
            await preloadBillImages(restaurant);

            setModalState((prev) => ({ ...prev, progressOverride: 'Building bill HTML…' }));

            const preparedBills = await Promise.all(
                billRows.map(async (element) => {
                    const orderId = element.orderId as string;
                    const pdfBaseName = getPdfBaseName(element);
                    updateItem(orderId, { status: 'processing', message: 'Preparing HTML…' });
                    const html = await resolveBillHtml(element, restaurant, isRoomReport);
                    return { orderId, pdfBaseName, html };
                })
            );

            setModalState((prev) => ({ ...prev, progressOverride: null }));

            await runWithConcurrency(preparedBills, BILL_PDF_CONCURRENCY, async ({ orderId, pdfBaseName, html }) => {
                updateItem(orderId, { status: 'processing', message: 'Creating PDF…' });

                try {
                    const blob = await generateBillPdfBlob(
                        html,
                        { title: pdfBaseName, author },
                        isRoomReport
                    );
                    const fileName = allocateUniquePdfFileName(pdfBaseName, usedFileNames);
                    zip.file(fileName, blob);
                    succeeded += 1;
                    updateItem(orderId, {
                        status: 'done',
                        message: 'Added to archive',
                        detail: fileName,
                    });
                } catch (error: unknown) {
                    failed += 1;
                    const message = error instanceof Error ? error.message : 'Failed to generate PDF';
                    errors.push({ id: orderId, message });
                    updateItem(orderId, { status: 'error', message });
                }
            });

            if (succeeded > 0) {
                setModalState((prev) => ({
                    ...prev,
                    progressOverride: 'Building ZIP archive…',
                }));
                const content = await zip.generateAsync({
                    type: 'blob',
                    compression: 'STORE',
                });
                saveAs(content, buildZipFilename(isRoomReport, startDate, endDate));
            }

            const summary: ReportProgressSummary = {
                total: billRows.length,
                succeeded,
                skipped: 0,
                failed,
            };

            setModalState((prev) => ({
                ...prev,
                phase: 'complete',
                progressOverride: null,
                summary,
                items: prev.items.map((item) => {
                    const errorEntry = errors.find((e) => e.id === item.id);
                    if (errorEntry) {
                        return { ...item, status: 'error' as const, message: errorEntry.message };
                    }
                    return item;
                }),
            }));

            if (failed > 0) {
                dispatch(logError({
                    severity: 'error',
                    message: `Bill download: ${failed} failed, ${succeeded} generated.`,
                }));
            } else if (succeeded > 0) {
                dispatch(logError({
                    severity: 'success',
                    message: `Downloaded ${succeeded} bill(s) as ZIP.`,
                }));
            }
        } catch (error) {
            console.error('Error downloading bills:', error);
            dispatch(logError({
                severity: 'error',
                message: (error as Error)?.message || 'Failed to download bills',
            }));
            setModalState((prev) => ({ ...prev, phase: 'complete', progressOverride: null }));
        } finally {
            inFlightRef.current = false;
            setIsDownloading(false);
        }
    };

    const handleClose = () => {
        if (inFlightRef.current) {
            return;
        }
        setModalState(INITIAL_MODAL_STATE);
    };

    const billTypeLabel = isRoomReport ? 'room' : 'food';

    const modal = React.createElement(ReportProgressModal, {
        open: modalState.open,
        phase: modalState.phase,
        title: 'Download Bills',
        subtitle: `Generate PDF bills for all ${billTypeLabel} orders in this report`,
        headerIcon: React.createElement(PictureAsPdfRoundedIcon),
        stepLabels: ['Review', 'Generate', 'Done'],
        alertContent: React.createElement(
            'p',
            null,
            'Creates a PDF for each ',
            React.createElement('strong', null, String(modalState.items.length)),
            ` ${billTypeLabel} bill in the current report and downloads them as a single ZIP file.`
        ),
        listSectionLabel: 'Bills to generate',
        items: modalState.items,
        summary: modalState.summary,
        confirmLabel: 'Start download',
        completeProgressLabel: 'Download complete',
        startProgressLabel: 'Starting PDF generation…',
        runningProgressLabel: 'Generating',
        runningButtonLabel: 'Generating bills…',
        successStatLabel: 'Generated',
        detailPrefix: 'File',
        progressOverride: modalState.progressOverride,
        onConfirm: handleConfirm,
        onClose: handleClose,
    });

    return {
        isDownloading,
        openDownloadBills,
        downloadBillsModal: modal,
    };
};
